import type { BuildOptions, GuidedBuildPreview } from "./api.js";
import type { GeneratedCharacterPayload } from "./character-generator.js";
import { generateStartingPossessions } from "./starting-possessions.js";

export interface CharacterSheetExportPayload {
  build: {
    rulesetId: "yokai-core-draft";
    speciesId: string;
    originId: string;
    attributeFreePoints: Record<string, number>;
    generalSkillFreePoints: Record<string, number>;
    monstrousSkillFreePoints: Record<string, number>;
    arcanePowerIds: string[];
    initialFearPoints: number;
  };
  identity: {
    characterName: string;
    playerName: string;
    speciesId: string;
    speciesName: string;
    clanName: string;
    archetypes: string[];
    portraitDataUrl?: string;
  };
  mechanics: {
    attributes: Record<string, number>;
    resources: Record<string, number>;
    generalSkills: Record<string, number>;
    monstrousSkills: Record<string, number>;
    arcanePowers: Array<{ id: string; name: string; sphere: string; fearCost: number | null }>;
    racialPowers: Array<{ id: string; name: string; fearCost: number | null }>;
    powerDescriptions: Array<{ id: string; type: string; name: string; description: string | null }>;
  };
  equipment: {
    inventory: string[];
    contacts: string[];
    yen: string;
    armor: string;
    weapons: Array<Record<string, never>>;
  };
  narrative: {
    backstory: string;
    appearance: string;
    notes: string;
  };
  discord: {
    originId: string;
    buildLabel: string;
    rumor: string;
    goal: string;
    bond: string;
  };
}

function numericRecord(value: unknown): Record<string, number> {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  return Object.fromEntries(Object.entries(value).filter((entry): entry is [string, number] => typeof entry[1] === "number" && Number.isFinite(entry[1])));
}

function nestedRecord(value: unknown, key: string): Record<string, number> {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  return numericRecord((value as Record<string, unknown>)[key]);
}

function humanizeRuleId(id: string): string {
  const segment = id.split(".").at(-1) ?? id;
  return segment.replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

export function buildCharacterSheetExport(
  options: BuildOptions,
  preview: GuidedBuildPreview,
  payload: GeneratedCharacterPayload,
): CharacterSheetExportPayload {
  const attributeFreePoints = numericRecord(payload.attributeFreePoints);
  const attributeModifiers = numericRecord(options.species.attribute_modifiers);
  const baseAttributes = nestedRecord(options.package.rules, "baseAttributes");
  const attributes = Object.fromEntries(options.attributes.map((attribute) => [
    attribute.id,
    (baseAttributes[attribute.id] ?? 10) + (attributeFreePoints[attribute.id] ?? 0) + (attributeModifiers[attribute.id] ?? 0),
  ]));

  const generalSkills = numericRecord(payload.generalSkillFreePoints);
  const monstrousFreePoints = numericRecord(payload.monstrousSkillFreePoints);
  const monstrousModifiers = numericRecord(options.species.starting_monstrous_skill_modifiers);
  const monstrousSkillIds = new Set([...Object.keys(monstrousFreePoints), ...Object.keys(monstrousModifiers)]);
  const monstrousSkills = Object.fromEntries([...monstrousSkillIds].map((id) => [
    id,
    (monstrousFreePoints[id] ?? 0) + (monstrousModifiers[id] ?? 0),
  ]));

  const powerById = new Map(options.arcanePowers.map((power) => [power.id, power]));
  const arcanePowers = payload.arcanePowerIds.map((id) => {
    const power = powerById.get(id);
    return {
      id,
      name: power?.label || humanizeRuleId(id),
      sphere: power?.sphere_id?.replace("sphere.", "") || "",
      fearCost: power?.fear_cost ?? null,
    };
  });

  // The public export API replaces these fallback labels and costs with the
  // canonical database rows before rendering the official sheet.
  const racialPowers = (options.species.racial_power_ids ?? []).map((id) => ({
    id,
    name: humanizeRuleId(id),
    fearCost: null,
  }));

  const totalFormPoints = (attributes["attribute.resistance"] ?? 0) + (attributes["attribute.arcane"] ?? 0);
  const initialFearPoints = typeof payload.initialFearPoints === "number"
    ? payload.initialFearPoints
    : options.allocation.initialFearPoints;
  const originLabel = options.origins.find((origin) => origin.id === payload.originId)?.label || humanizeRuleId(payload.originId);
  const contacts = [payload.bond, payload.rumor].filter((value): value is string => typeof value === "string" && value.trim().length > 0);
  const notes = [payload.notes, payload.goal ? `Goal: ${payload.goal}` : "", payload.clanDescription].filter(Boolean).join("\n\n");
  const portraitDataUrl = typeof payload.portraitDataUrl === "string" && payload.portraitDataUrl
    ? payload.portraitDataUrl
    : undefined;
  const buildLabel = `${preview.profile.generalSkillLabel} · ${preview.profile.attributeLabel}`;
  const startingPossessions = generateStartingPossessions({
    speciesId: options.species.id,
    originId: payload.originId,
    clanName: payload.clan,
    characterName: payload.characterName,
    buildLabel,
  });

  return {
    build: {
      rulesetId: "yokai-core-draft",
      speciesId: options.species.id,
      originId: payload.originId,
      attributeFreePoints,
      generalSkillFreePoints: generalSkills,
      monstrousSkillFreePoints: monstrousFreePoints,
      arcanePowerIds: [...payload.arcanePowerIds],
      initialFearPoints,
    },
    identity: {
      // The builder still displays the generated kanji. The current official
      // English PDF uses a Latin font, so only the Romanized name is sent to
      // the form field; this also fixes the reported Japanese-glyph HTTP 500.
      characterName: payload.characterName,
      playerName: payload.playerName,
      speciesId: options.species.id,
      speciesName: options.species.label,
      clanName: payload.clan,
      archetypes: originLabel ? [originLabel] : [],
      ...(portraitDataUrl ? { portraitDataUrl } : {}),
    },
    mechanics: {
      attributes,
      resources: {
        fearPoints: initialFearPoints,
        famePoints: 0,
        madnessPoints: 0,
        reincarnations: Math.floor(totalFormPoints / 100),
        currentFormPoints: totalFormPoints,
        totalFormPoints,
      },
      generalSkills,
      monstrousSkills,
      arcanePowers,
      racialPowers,
      powerDescriptions: arcanePowers.map((power) => ({
        id: power.id,
        type: "arcane",
        name: power.name,
        description: powerById.get(power.id)?.short_description ?? null,
      })),
    },
    equipment: {
      inventory: startingPossessions.inventory,
      contacts,
      yen: startingPossessions.yen,
      armor: startingPossessions.armor,
      weapons: startingPossessions.weapons,
    },
    narrative: {
      backstory: payload.background,
      appearance: payload.appearance,
      notes,
    },
    discord: {
      originId: payload.originId,
      buildLabel,
      rumor: payload.rumor,
      goal: payload.goal,
      bond: payload.bond,
    },
  };
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1_000);
}

export function safeExportFilename(value: string): string {
  const cleaned = value.normalize("NFKD").replace(/[^a-zA-Z0-9._-]+/g, "-").replace(/^-+|-+$/g, "");
  return cleaned || "yokai-character";
}
